import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { Alert, EmptyState, Modal } from '@ds/feedback';
import { Radio } from '@ds/forms';
import { dataEHoraPorExtenso } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { avisoDaConta } from '@/data/anuncios';
import { dados } from '@/data/porta';
import type { ContaDaMeta, ContaDeAnuncioDaClinica } from '@/data/tipos';

const TEXTO = { margin: 0, fontSize: 13, lineHeight: '19px', color: 'var(--text-secondary)' } as const;

/**
 * As ações da conexão com a Meta (0162), iguais em Integrações e no painel.
 *
 * Conectar leva ao login da Meta (fora do app); a demonstração devolve um caminho
 * interno, e aí a navegação é do roteador.
 */
export function useConexaoDaMeta(aoMudar: () => void, irPara?: (url: string) => void) {
    const aviso = useAviso();
    const navigate = useNavigate();
    const [confirmar, confirmacao] = useConfirmacao();
    const [ocupado, setOcupado] = useState<'conectar' | 'sincronizar' | 'desconectar' | null>(null);

    const ir = irPara ?? ((url: string) => (url.startsWith('/') ? navigate(url) : window.location.assign(url)));

    const conectar = async (destino: string) => {
        setOcupado('conectar');
        try {
            ir(await dados().conectarMeta(destino));
        } catch (e) {
            aviso({ title: 'Não foi possível abrir o login da Meta', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setOcupado(null);
        }
    };

    const sincronizar = async () => {
        setOcupado('sincronizar');
        try {
            const r = await dados().sincronizarMeta();
            aviso({ title: 'Números atualizados', description: `${r.campanhas} ${r.campanhas === 1 ? 'campanha' : 'campanhas'} lidas da Meta` });
            aoMudar();
        } catch (e) {
            aviso({ title: 'Não foi possível atualizar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
            aoMudar();
        } finally {
            setOcupado(null);
        }
    };

    const desconectar = async (conta: ContaDeAnuncioDaClinica) => {
        const ok = await confirmar({
            titulo: 'Desconectar a Meta Ads?',
            descricao: `${conta.nome ?? 'A conta'} deixa de ser lida. Campanhas e números guardados aqui são apagados; os leads continuam, sem a campanha. Nada muda na Meta.`,
            confirmar: 'Desconectar',
        });
        if (!ok) return;
        setOcupado('desconectar');
        try {
            await dados().desconectarMeta();
            aviso({ title: 'Meta Ads desconectada' });
            aoMudar();
        } catch (e) {
            aviso({ title: 'Não foi possível desconectar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        } finally {
            setOcupado(null);
        }
    };

    return { conectar, sincronizar, desconectar, ocupado, confirmacao };
}

/** Escolher qual conta de anúncios é a da clínica, entre as que o login alcança. */
export function EscolherContaDaMeta({ aberto, atual, onFechar, onEscolhida }: {
    aberto: boolean;
    atual?: string;
    onFechar: () => void;
    onEscolhida: () => void;
}) {
    const aviso = useAviso();
    const [contas, setContas] = useState<ContaDaMeta[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [escolha, setEscolha] = useState<string>(atual ?? '');
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        if (!aberto) return undefined;
        let vivo = true;
        setContas(null);
        setErro(null);
        setEscolha(atual ?? '');
        dados().contasDaMeta()
            .then((c) => {
                if (!vivo) return;
                setContas(c);
                if (!atual && c.length === 1 && c[0]) setEscolha(c[0].id);
            })
            .catch((e: unknown) => { if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível listar as contas.'); });
        return () => { vivo = false; };
    }, [aberto, atual]);

    const salvar = async () => {
        setSalvando(true);
        setErro(null);
        try {
            await dados().escolherContaDaMeta(escolha);
            aviso({ title: 'Conta de anúncios escolhida', description: contas?.find((c) => c.id === escolha)?.nome, tone: 'success' });
            onEscolhida();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível usar esta conta.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open={aberto} width={520} title="Qual conta de anúncios é a da clínica?" onClose={onFechar}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void salvar(); }} disabled={!escolha || escolha === atual || salvando}>
                        {salvando ? 'Lendo os números…' : 'Usar esta conta'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={TEXTO}>
                    São as contas que o seu login da Meta alcança. A escolhida passa a ser lida todo dia; trocar depois apaga
                    os números guardados da anterior.
                </p>
                {erro && <Alert tone="danger" dismissible={false}>{erro}</Alert>}
                {!contas && !erro && <p style={TEXTO}>Buscando as contas na Meta…</p>}
                {contas?.length === 0 && (
                    <EmptyState compact icon="search-x" title="Nenhuma conta de anúncios" description="O login da Meta não tem acesso a nenhuma conta. Peça acesso no Gerenciador de Negócios e conecte de novo." />
                )}
                {contas && contas.length > 0 && (
                    <div role="radiogroup" aria-label="Contas de anúncios" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {contas.map((c) => (
                            <Radio
                                key={c.id} name="conta-meta" checked={escolha === c.id} onChange={() => setEscolha(c.id)}
                                label={c.nome}
                                description={`${c.id.replace('act_', 'ID ')} · ${c.moeda}${c.ativa ? '' : ' · desativada na Meta'}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        </Modal>
    );
}

/** O cartão da Meta em Configurações › Integrações. */
export function CartaoDaMeta({ disponivel, conta, podeGerir, escolherAgora, aoMudar }: {
    disponivel: boolean;
    conta: ContaDeAnuncioDaClinica | null;
    podeGerir: boolean;
    /** A pessoa voltou do login com mais de uma conta: o seletor já abre. */
    escolherAgora?: boolean;
    aoMudar: () => void;
}) {
    const { conectar, sincronizar, desconectar, ocupado, confirmacao } = useConexaoDaMeta(aoMudar);
    const [escolhendo, setEscolhendo] = useState(false);
    useEffect(() => {
        if (escolherAgora && conta?.situacao === 'escolhendo' && podeGerir) setEscolhendo(true);
    }, [escolherAgora, conta?.situacao, podeGerir]);

    const aviso = conta ? avisoDaConta(conta) : null;
    const destino = '/app/configuracoes/integracoes';

    return (
        <Card
            title="Anúncios · Meta Ads"
            subtitle="Facebook e Instagram: investimento, cliques e leads por campanha, cruzados com o CRM"
            padding={18}
            actions={conta ? (
                <Badge tone={conta.situacao === 'conectada' ? 'success' : conta.situacao === 'revogada' ? 'danger' : 'warning'} dot>
                    {conta.situacao === 'conectada' ? 'Conectada' : conta.situacao === 'revogada' ? 'Sem acesso' : conta.situacao === 'erro' ? 'Com erro' : 'Falta a conta'}
                </Badge>
            ) : undefined}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {!disponivel && (
                    <p style={TEXTO}>A Fórmula MED ainda está configurando a integração com a Meta. Assim que estiver pronta, a conexão aparece aqui.</p>
                )}
                {disponivel && !conta && (
                    <p style={TEXTO}>
                        Entre com a conta da Meta que administra os anúncios da clínica e escolha a conta de anúncios. A leitura
                        é só de números: nada é criado nem mudado nos anúncios.
                    </p>
                )}
                {conta && (
                    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 14px', fontSize: 13 }}>
                        <dt style={{ color: 'var(--text-tertiary)' }}>Conta</dt>
                        <dd style={{ margin: 0 }}>{conta.nome ?? '—'}{conta.moeda ? ` · ${conta.moeda}` : ''}</dd>
                        <dt style={{ color: 'var(--text-tertiary)' }}>Conectada por</dt>
                        <dd style={{ margin: 0 }}>{conta.conectadaPor ?? conta.usuarioExterno ?? '—'}{conta.conectadaEm ? ` em ${dataEHoraPorExtenso(conta.conectadaEm)}` : ''}</dd>
                        <dt style={{ color: 'var(--text-tertiary)' }}>Última leitura</dt>
                        <dd style={{ margin: 0 }}>{conta.ultimaSincronizacaoEm ? dataEHoraPorExtenso(conta.ultimaSincronizacaoEm) : 'ainda não'}</dd>
                    </dl>
                )}
                {aviso && <Alert tone={aviso.tom} title={aviso.titulo} dismissible={false}>{aviso.texto}</Alert>}
                {!podeGerir && disponivel && (
                    <p style={TEXTO}>Conectar e trocar a conta exige a permissão de marketing.</p>
                )}
                {podeGerir && disponivel && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {(!conta || conta.situacao === 'revogada' || aviso?.titulo.startsWith('O login')) && (
                            <Button iconLeft={<Icon name="log-in" size={14} />} loading={ocupado === 'conectar'} onClick={() => { void conectar(destino); }}>
                                {conta ? 'Conectar de novo' : 'Conectar Meta Ads'}
                            </Button>
                        )}
                        {conta && conta.situacao !== 'revogada' && (
                            <Button variant="secondary" iconLeft={<Icon name="list-checks" size={14} />} onClick={() => setEscolhendo(true)}>
                                {conta.contaExternaId ? 'Trocar a conta' : 'Escolher a conta'}
                            </Button>
                        )}
                        {conta?.contaExternaId && conta.situacao !== 'revogada' && (
                            <Button variant="secondary" iconLeft={<Icon name="refresh-cw" size={14} />} loading={ocupado === 'sincronizar'} onClick={() => { void sincronizar(); }}>
                                Atualizar agora
                            </Button>
                        )}
                        {conta && (
                            <Button variant="ghost" loading={ocupado === 'desconectar'} onClick={() => { void desconectar(conta); }}>Desconectar</Button>
                        )}
                    </div>
                )}
            </div>
            <EscolherContaDaMeta
                aberto={escolhendo} {...(conta?.contaExternaId ? { atual: conta.contaExternaId } : {})}
                onFechar={() => setEscolhendo(false)}
                onEscolhida={() => { setEscolhendo(false); aoMudar(); }}
            />
            {confirmacao}
        </Card>
    );
}
